// Achievement Notification Component
// Hiển thị thông báo khi unlock achievement

import { useEffect, useState } from 'react';
import { Achievement } from '../../types/learningHistory';
import { X } from 'lucide-react';

interface AchievementNotificationProps {
  achievements: Achievement[];
  onClose?: () => void;
  autoHideDelay?: number; // milliseconds
}

export default function AchievementNotification({
  achievements,
  onClose,
  autoHideDelay = 5000
}: AchievementNotificationProps) {
  const [visible, setVisible] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (achievements.length === 0) return;

    // Auto hide after delay
    const timer = setTimeout(() => {
      if (currentIndex < achievements.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        handleClose();
      }
    }, autoHideDelay);

    return () => clearTimeout(timer);
  }, [currentIndex, achievements.length, autoHideDelay]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300); // Wait for animation
  };

  if (!visible || achievements.length === 0) return null;

  const achievement = achievements[currentIndex];

  return (
    <div className="fixed top-4 right-4 z-50 animate-slideInRight">
      <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500 rounded-xl shadow-2xl p-6 max-w-sm border-4 border-yellow-300 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}/>
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 p-1 hover:bg-yellow-600/20 rounded-full transition-colors z-10"
        >
          <X className="w-4 h-4 text-yellow-900" />
        </button>

        {/* Content */}
        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-center gap-3 mb-3">
            <div className="text-5xl animate-bounce">🏆</div>
            <div>
              <h3 className="text-lg font-bold text-yellow-900">
                Thành tích mới!
              </h3>
              <p className="text-xs text-yellow-800">
                {currentIndex + 1} / {achievements.length}
              </p>
            </div>
          </div>

          {/* Achievement details */}
          <div className="bg-white/30 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-4xl flex-shrink-0 animate-pulse">
                {achievement.icon}
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-yellow-900 mb-1">
                  {achievement.title}
                </h4>
                <p className="text-sm text-yellow-800">
                  {achievement.description}
                </p>
                {achievement.metadata && (
                  <div className="mt-2 text-xs text-yellow-700">
                    {achievement.metadata.streak && (
                      <span>Streak: {achievement.metadata.streak} ngày</span>
                    )}
                    {achievement.metadata.totalQuizzes && (
                      <span>Tổng: {achievement.metadata.totalQuizzes} bài</span>
                    )}
                    {achievement.metadata.score && (
                      <span>Điểm: {achievement.metadata.score}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Progress indicator */}
          {achievements.length > 1 && (
            <div className="flex gap-1 mt-3 justify-center">
              {achievements.map((_, index) => (
                <div
                  key={index}
                  className={`h-1.5 rounded-full transition-all ${
                    index === currentIndex
                      ? 'w-8 bg-yellow-900'
                      : 'w-1.5 bg-yellow-700/40'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Shine effect */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent animate-shine"/>
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes shine {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(200%);
          }
        }

        .animate-slideInRight {
          animation: slideInRight 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .animate-shine {
          animation: shine 3s infinite;
        }
      `}</style>
    </div>
  );
}

// Multi-achievement toast (show all at once in a list)
interface AchievementToastListProps {
  achievements: Achievement[];
  onClose?: () => void;
}

export function AchievementToastList({
  achievements,
  onClose
}: AchievementToastListProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onClose?.(), 300);
    }, 8000);

    return () => clearTimeout(timer);
  }, [onClose]);

  if (!visible || achievements.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {achievements.map((achievement, index) => (
        <div
          key={achievement.id}
          className="bg-gradient-to-r from-yellow-400 to-amber-500 rounded-lg shadow-xl p-4 border-2 border-yellow-300 animate-slideInRight"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <div className="flex items-center gap-3">
            <div className="text-3xl">{achievement.icon}</div>
            <div className="flex-1">
              <h4 className="font-bold text-yellow-900 text-sm">
                {achievement.title}
              </h4>
              <p className="text-xs text-yellow-800">
                {achievement.description}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

